// Click the colored dot in a Department lane header to swap its color.
// Opens a small palette popover (same `DEPARTMENT_CUSTOM_PALETTE` we cycle
// through when creating a new department) and updates the swatch + edge
// color together so the lane header's dot and the cards' accent stay
// visually consistent.

import { useRef, useState } from 'react';
import { DEPARTMENT_CUSTOM_PALETTE } from '../../pm/constants';
import { updateProjectDepartment } from '../../pm/firebase';
import PortalMenu from './PortalMenu';

interface Props {
  departmentId: string;
  /** Currently-selected swatch — drives the "is-on" highlight in the menu
   *  and what color the trigger dot paints. */
  swatch: string;
}

const DepartmentColorPicker: React.FC<Props> = ({ departmentId, swatch }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  async function pick(next: { swatch: string; color: string }) {
    setOpen(false);
    // Errors here surface through the realtime subscription (the dot stays
    // whatever the canonical store says). Logging keeps the failure mode
    // debuggable in DevTools.
    try {
      await updateProjectDepartment(departmentId, next);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('updateProjectDepartment failed', err);
    }
  }

  return (
    <>
      <button
        ref={triggerRef as unknown as React.RefObject<HTMLButtonElement>}
        type="button"
        className="pm-dept-dot-picker"
        title="Change color"
        aria-label="Change department color"
        onClick={(e) => {
          // Lane header has its own click handlers (drag, etc) — keep this
          // contained to the picker.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{ background: swatch }}
      />
      <PortalMenu
        open={open}
        triggerRef={triggerRef as unknown as React.RefObject<HTMLElement>}
        onDismiss={() => setOpen(false)}
        className="pm-dept-color-menu"
        // Grid of 6 swatches → estimate based on rough dimensions; the
        // measured height kicks in on next frame anyway.
        estimatedHeight={120}
      >
        <div className="pm-dept-color-grid" role="listbox" aria-label="Department color">
          {DEPARTMENT_CUSTOM_PALETTE.map((opt) => {
            const isOn = opt.swatch.toLowerCase() === swatch.toLowerCase();
            return (
              <button
                key={opt.swatch}
                type="button"
                role="option"
                aria-selected={isOn}
                className={`pm-dept-color-swatch${isOn ? ' is-on' : ''}`}
                style={{ background: opt.swatch }}
                title={opt.swatch}
                onClick={() => pick(opt)}
              />
            );
          })}
        </div>
      </PortalMenu>
    </>
  );
};

export default DepartmentColorPicker;
